/**
 * useCategories Hook
 *
 * 카테고리 목록을 조회하는 Hook입니다.
 */

import { useState, useEffect } from 'react'
import { categoryRepository } from '../repositories'
import type { Category } from '../types/Category'

interface UseCategoriesReturn {
  /**
   * 지출 카테고리 목록 (대분류, children에 소분류 포함)
   */
  expenseCategories: Category[]

  /**
   * 수입 카테고리 목록 (대분류, children에 소분류 포함)
   */
  incomeCategories: Category[]

  /**
   * 로딩 상태
   */
  loading: boolean

  /**
   * 에러 정보
   */
  error: Error | null

  /**
   * 데이터 재조회
   */
  refetch: () => Promise<void>
}

/**
 * 카테고리 목록 조회 Hook
 *
 * @returns 지출/수입 카테고리 목록, 로딩 상태, 에러, refetch 함수
 *
 * @example
 * ```tsx
 * function CategorySelector() {
 *   const { expenseCategories, incomeCategories, loading, error } = useCategories()
 *
 *   if (loading) return <div>로딩 중...</div>
 *   if (error) return <div>에러: {error.message}</div>
 *
 *   return (
 *     <select>
 *       {expenseCategories.map(cat => (
 *         <optgroup key={cat.id} label={cat.name}>
 *           {cat.children?.map(child => (
 *             <option key={child.id} value={child.id}>{child.name}</option>
 *           ))}
 *         </optgroup>
 *       ))}
 *     </select>
 *   )
 * }
 * ```
 */
export function useCategories(): UseCategoriesReturn {
  const [expenseCategories, setExpenseCategories] = useState<Category[]>([])
  const [incomeCategories, setIncomeCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const fetchCategories = async () => {
    try {
      setLoading(true)
      setError(null)

      // 병렬로 지출/수입 카테고리 조회
      const [expense, income] = await Promise.all([
        categoryRepository.findByType('expense'),
        categoryRepository.findByType('income'),
      ])

      setExpenseCategories(expense)
      setIncomeCategories(income)
    } catch (err) {
      setError(
        err instanceof Error
          ? err
          : new Error('카테고리 목록을 불러오는데 실패했습니다')
      )
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchCategories()
  }, [])

  return {
    expenseCategories,
    incomeCategories,
    loading,
    error,
    refetch: fetchCategories,
  }
}
